/**
 * JWT Utility Functions
 *
 * Client-side JWT parsing and expiration checking.
 * Frontend ONLY validates expiration — signature validation is done by the backend.
 */

export interface JwtPayload {
  exp?: number
  iat?: number
  sub?: string
  [claim: string]: unknown
}

export function base64UrlDecode(str: string): string | null {
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/')

  const padding = base64.length % 4
  if (padding) {
    base64 += '='.repeat(4 - padding)
  }

  try {
    const decoded = atob(base64)

    return decodeURIComponent(
      decoded
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
  } catch (error) {
    console.error('Failed to decode base64url:', error)
    return null
  }
}

export function parseJWT(token: string | null | undefined): JwtPayload | null {
  if (!token || typeof token !== 'string') {
    return null
  }

  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      return null
    }

    const payload = base64UrlDecode(parts[1])
    if (!payload) {
      return null
    }

    return JSON.parse(payload) as JwtPayload
  } catch (error) {
    console.error('Failed to parse JWT:', error)
    return null
  }
}

export function getTokenExpTimestamp(token: string | null | undefined): number | null {
  const payload = parseJWT(token)
  return payload?.exp ?? null
}

export function getTokenIatTimestamp(token: string | null | undefined): number | null {
  const payload = parseJWT(token)
  return payload?.iat ?? null
}

export function calculateTokenFreshness(token: string | null | undefined): number {
  const payload = parseJWT(token)
  if (!payload || !payload.exp || !payload.iat) {
    return 0
  }

  const now = Math.floor(Date.now() / 1000)
  const issuedAt = payload.iat
  const expiresAt = payload.exp
  const totalLifetime = expiresAt - issuedAt
  const remaining = expiresAt - now

  if (remaining <= 0) {
    return 0
  }

  const percentageRemaining = (remaining / totalLifetime) * 100
  return Math.max(0, Math.min(100, percentageRemaining))
}

export function isTokenExpired(token: string | null | undefined): boolean {
  const exp = getTokenExpTimestamp(token)
  if (!exp) return true

  const now = Math.floor(Date.now() / 1000)
  return now >= exp
}

export function isTokenExpiringSoon(token: string | null | undefined): boolean {
  const freshness = calculateTokenFreshness(token)
  return freshness > 0 && freshness <= 20
}

export function getTokenExpirationDate(token: string | null | undefined): string {
  const exp = getTokenExpTimestamp(token)
  if (!exp) return 'Invalid token'

  return new Date(exp * 1000).toLocaleString()
}

export function getTokenRemainingSeconds(token: string | null | undefined): number {
  const exp = getTokenExpTimestamp(token)
  if (!exp) return 0

  const now = Math.floor(Date.now() / 1000)
  const remaining = exp - now

  return Math.max(0, remaining)
}
